<template>
  <b-card>
    <b-card-title class="d-flex flex-column flex-md-row justify-content-between align-md-items-center">
      <span class="mb-2">Product Outcomes</span>
      <b-form-group class="mb-0">
        <b-form-radio-group
          v-model="selectedChart"
          :options="chartOptions"
          name="distribution-chart-selection"
          button-variant="outline-primary"
          buttons
        />
      </b-form-group>
    </b-card-title>
    <b-card-sub-title class="pb-00">
      <span v-show="selectedChart === 'outcomes'">
        Current distribution of outcomes at maturity
      </span>
      <span v-show="selectedChart === 'returns'">
        Current distribution of returns at maturity
      </span>
    </b-card-sub-title>
    <b-card-text>
      <div v-if="loading" style="min-height: 370px;">
        <loading-container />
      </div>
      <div v-else style="min-height: 370px; height: 370px;">
        <app-echart-bar
          ref="barChart"
          :key="chartKey"
          :option-data="selectedChart === 'outcomes' ? outcomesChartData : returnsChartData"
          style="height: 100%; width: 100%;"
        />
      </div>
    </b-card-text>
  </b-card>
</template>

<script>
import {
  BCard, BCardTitle, BCardSubTitle, BCardText, BFormGroup, BFormRadioGroup,
} from 'bootstrap-vue'
import AppEchartBar from '@core/components/charts/echart/AppEchartBar.vue'
import useAppConfig from '@core/app-config/useAppConfig'
import LoadingContainer from '@/views/components/LoadingContainer.vue'
import renderChartToBase64 from './offscreenRender'

export default {
  components: {
    BCard,
    BCardTitle,
    BCardSubTitle,
    BCardText,
    BFormGroup,
    BFormRadioGroup,
    AppEchartBar,
    LoadingContainer,
  },
  props: {
    returnChartAbs: {
      type: Object,
      default: () => ({
        XAXIS: [],
        SHOWDATA: [],
      }),
    },
    returnChartAnn: {
      type: Object,
      default: () => ({
        XAXIS: [],
        SHOWDATA: [],
      }),
    },
  },
  setup() {
    const { skin } = useAppConfig()
    return { skin }
  },
  data() {
    return {
      loading: true,
      selectedChart: 'outcomes',
      chartOptions: [
        { text: 'Outcomes', value: 'outcomes' },
        { text: 'Returns', value: 'returns' },
      ],
      chartKey: 0,
      outcomesChartData: {},
      returnsChartData: {},
      cachedOutcomesImage: null,
      cachedReturnsImage: null,
    }
  },
  computed: {
    splitLineOpacity() {
      return this.skin === 'dark' ? 0.1 : 1
    },
  },
  watch: {
    returnChartAbs: {
      handler() {
        this.processDistributionData()
      },
      immediate: true,
      deep: true,
    },
    returnChartAnn: {
      handler() {
        this.processDistributionData()
      },
      immediate: true,
      deep: true,
    },
    selectedChart() {
      // Increment key to force chart re-render when switching charts
      this.chartKey += 1
    },
    skin() {
      // Re-process data when theme changes
      this.processDistributionData()
    },
  },
  methods: {
    getDataURL() {
      return (this.selectedChart === 'outcomes' ? this.cachedOutcomesImage : this.cachedReturnsImage) || null
    },
    getBothDataURLs() {
      return {
        outcomes: this.cachedOutcomesImage || null,
        returns: this.cachedReturnsImage || null,
      }
    },
    processDistributionData() {
      // Outcomes Chart (Absolute Returns)
      this.outcomesChartData = this.createChartData(
        this.returnChartAbs,
        'Outcomes',
        ['#28a745', '#ffc107', '#dc3545'], // green, yellow, red
      )

      // Returns Chart (Annualized Returns)
      this.returnsChartData = this.createChartData(
        this.returnChartAnn,
        'Returns',
        ['#28a745', '#ffc107', '#dc3545'],
      )

      this.cachedOutcomesImage = renderChartToBase64(this.outcomesChartData)
      this.cachedReturnsImage = renderChartToBase64(this.returnsChartData)
      this.loading = false
    },
    createChartData(chartData, chartName, colors) {
      if (!chartData || !chartData.SHOWDATA || chartData.SHOWDATA.length === 0) {
        return {}
      }

      // Process series data - SHOWDATA is an array of series
      const series = chartData.SHOWDATA.map((seriesItem, index) => ({
        name: seriesItem.name || `Series ${index + 1}`,
        type: 'bar',
        stack: 'total',
        data: seriesItem.data ? seriesItem.data.map(val => Math.round(parseFloat(val) * 100) / 100) : [],
        itemStyle: {
          color: colors[index] || 'lightblue',
        },
      }))

      return {
        legend: {
          show: true,
          top: 10,
          left: 'center',
        },
        grid: {
          left: 60,
          right: 20,
          bottom: 60,
          top: 50,
          containLabel: true,
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow',
          },
          formatter: params => {
            if (params && params.length > 0) {
              let result = `${params[0].name}<br/>`
              params.forEach(param => {
                result += `${param.marker} ${param.seriesName}: ${param.value}%<br/>`
              })
              return result
            }
            return ''
          },
        },
        xAxis: {
          type: 'category',
          data: chartData.XAXIS || [],
          axisLabel: {
            fontSize: 11,
            rotate: 45,
          },
          splitLine: {
            show: false,
          },
        },
        yAxis: {
          type: 'value',
          name: 'Probability (%)',
          nameLocation: 'middle',
          nameGap: 50,
          nameTextStyle: {
            fontSize: 13,
            fontWeight: 500,
          },
          axisLabel: {
            fontSize: 12,
          },
          splitLine: {
            lineStyle: {
              opacity: this.splitLineOpacity,
              type: 'dashed',
            },
          },
        },
        series,
      }
    },
  },
}
</script>
